// File: client/src/variants/zombie/zombieLogic.js
//
// Pure Zombie Chess logic (no React). Zombies are a CUSTOM piece registered with the
// engine as 'z' (black-controlled) / 'Z' (white-controlled):
//   - move one square in any direction (king-like),
//   - can capture (which spreads the infection),
//   - never give check-mate-relevant long-range threats (they attack only adjacent
//     squares, exactly like their movement),
//   - cannot promote or castle (the engine already excludes non-'p'/'k' letters).
//
// Revival: when a piece is captured, it is queued to rise as a zombie CONTROLLED BY
// THE CAPTURER after REVIVE_DELAY of the capturer's turns. Kings never become zombies
// (a king capture ends the game). Zombies are worth 0 points.

export const REVIVE_DELAY = 3;   // capturer's turns until a captured piece revives
export const ZOMBIE_WHITE = 'Z';
export const ZOMBIE_BLACK = 'z';

const KING_OFFSETS = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1], [0, 1],
  [1, -1], [1, 0], [1, 1],
];

/** Is this board letter a zombie? */
export function isZombie(letter) {
  return letter === ZOMBIE_WHITE || letter === ZOMBIE_BLACK;
}

/** Zombie letter for a color. */
export function zombieFor(color) {
  return color === 'white' ? ZOMBIE_WHITE : ZOMBIE_BLACK;
}

// ---- custom-piece definition registered with the engine ----
// Movement: king-like single steps onto empty or enemy squares.
export const zombiePieceDef = {
  glyph: '🧟',
  moves(board, pos, isWhite) {
    const out = [];
    for (const [dr, dc] of KING_OFFSETS) {
      const r = pos.row + dr;
      const c = pos.col + dc;
      if (r < 0 || r > 7 || c < 0 || c > 7) continue;
      const target = board[r][c];
      if (!target || target === '') {
        out.push({ row: r, col: c, type: 'normal' });
      } else {
        const targetIsWhite = target === target.toUpperCase();
        if (targetIsWhite !== isWhite) out.push({ row: r, col: c, type: 'capture' });
      }
    }
    return out;
  },
  // Attacks (for check detection) only the 8 adjacent squares.
  attack(board, from, to /* , isWhite */) {
    const dr = Math.abs(to.row - from.row);
    const dc = Math.abs(to.col - from.col);
    return dr <= 1 && dc <= 1 && (dr !== 0 || dc !== 0);
  },
};

/**
 * Base letter of a captured piece, used only to decide "was it a king?" Kings never
 * revive. (We don't restore the original piece type — everything comes back as a
 * generic zombie, per the infection theme.)
 */
export function isKing(letter) {
  return letter === 'K' || letter === 'k';
}

/**
 * Queue a revival for a captured piece.
 * @param {object[]} queue existing revival queue
 * @param {string} capturedPiece the letter that was captured
 * @param {string} capturerColor 'white' | 'black' — who will control the zombie
 * @param {{row,col}} square where it was captured (preferred revive square)
 * @returns {object[]} new queue (kings are NOT queued)
 */
export function queueRevival(queue, capturedPiece, capturerColor, square) {
  if (isKing(capturedPiece)) return queue; // kings don't become zombies
  return [
    ...queue,
    { color: capturerColor, turnsLeft: REVIVE_DELAY, square: { ...square } },
  ];
}

/**
 * Advance the revival queue by one of `color`'s turns: decrement entries owned by
 * `color`, and spawn any that reach 0 onto the board (mutating it). Returns the
 * remaining queue and the list of spawned zombies.
 *
 * Spawn placement: the stored square if empty, else the nearest empty square
 * (breadth-first ring search). If the board is full near it, the revival is dropped.
 *
 * @param {object[]} queue
 * @param {string[][]} board mutated in place
 * @param {string} color whose turn just occurred
 * @returns {{queue: object[], spawned: {row,col,piece}[]}}
 */
export function advanceRevivals(queue, board, color) {
  const remaining = [];
  const spawned = [];

  for (const entry of queue) {
    if (entry.color !== color) {
      remaining.push(entry);
      continue;
    }
    const turnsLeft = entry.turnsLeft - 1;
    if (turnsLeft > 0) {
      remaining.push({ ...entry, turnsLeft });
      continue;
    }
    // Spawn now.
    const spot = findEmptyNear(board, entry.square);
    if (spot) {
      board[spot.row][spot.col] = zombieFor(entry.color);
      spawned.push({ ...spot, piece: zombieFor(entry.color) });
    }
    // If no spot found, the revival is lost (documented rule).
  }

  return { queue: remaining, spawned };
}

/** Find the given square if empty, else the nearest empty square by ring distance. */
export function findEmptyNear(board, square) {
  if (isEmpty(board, square.row, square.col)) return { row: square.row, col: square.col };
  for (let radius = 1; radius < 8; radius++) {
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue; // ring only
        const r = square.row + dr;
        const c = square.col + dc;
        if (r >= 0 && r < 8 && c >= 0 && c < 8 && isEmpty(board, r, c)) {
          return { row: r, col: c };
        }
      }
    }
  }
  return null;
}

function isEmpty(board, r, c) {
  return !board[r][c] || board[r][c] === '';
}
